<template>
    <span>
        <span @click="startConfirmingPassword">
            <slot />
        </span>

        <dialog-modal :show="confirmingPassword" @close="closeModal">
            <template #title>
                {{ title }}
            </template>

            {{ content }}

            <div class="mt-12">
                <form @submit.prevent="confirmPassword">
                    <jet-input
                        ref="passwordRef"
                        v-model="form.password"
                        :horizontal="false"
                        :invalidFeedback="form.error"
                        :placeholder="trans('auth.general.password')"
                        autocomplete="current-password"
                        autofocus
                        class="mt-1 block"
                        type="password"
                        @keyup.enter="confirmPassword"
                    />
                </form>
            </div>

            <template #footer>
                <jet-button variant="secondary" @click="closeModal">
                    {{ trans('general.action.cancel') }}
                </jet-button>

                <jet-button :class="{ 'opacity-25': form.processing }" :is-processing="form.processing" class="ml-2" @click="confirmPassword">
                    {{ button }}
                </jet-button>
            </template>
        </dialog-modal>
    </span>
</template>

<script setup>
import { nextTick, reactive, ref, watch } from 'vue';
import JetButton from '@/Jetstream/Button.vue';
import JetInput from '@/Jetstream/Input.vue';
import DialogModal from '@/Jetstream/DialogModal.vue';
import { $http } from '@/bootstrap.js';
import { trans } from '@/mixins/i18n.js';

const props = defineProps({
    title: {
        default: 'Confirm Password',
    },
    content: {
        default: 'For your security, please confirm your password to continue.',
    },
    button: {
        default: 'Confirm',
    },
});

const emit = defineEmits(['confirmed']);

const confirmingPassword = ref(false);
const passwordRef = ref(null);
const form = reactive({
    password: '',
    error: '',
    processing: false,
});

watch(confirmingPassword, (isShowing) => {
    if (isShowing) {
        nextTick(() => passwordRef.value?.$el?.querySelector('input')?.focus());
    }
});

function startConfirmingPassword() {
    $http.get(route('password.confirmation')).then((response) => {
        if (response.data.confirmed) {
            emit('confirmed');
        } else {
            confirmingPassword.value = true;
        }
    });
}

function confirmPassword() {
    form.processing = true;

    $http
        .post(route('password.confirm'), { password: form.password })
        .then(() => {
            form.processing = false;
            closeModal();
            nextTick(() => emit('confirmed'));
        })
        .catch((error) => {
            form.processing = false;
            form.error = error.response?.data?.errors?.password?.[0] ?? '';
            passwordRef.value?.$el?.querySelector('input')?.focus();
        });
}

function closeModal() {
    confirmingPassword.value = false;
    form.password = '';
    form.error = '';
}

defineExpose({ startConfirmingPassword });
</script>
